import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Monitor,
  ArrowLeft,
  RefreshCw,
  Maximize2,
  Pause,
  Flag,
  Clock,
  Users,
  Car,
  Grid3X3,
  List,
  CheckCircle2,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSeries } from '../../App';

// Race statuses
const raceStatuses = {
  upcoming: { label: 'Upcoming', color: 'text-slate-600 bg-slate-100', icon: Clock },
  staging: { label: 'Staging', color: 'text-amber-600 bg-amber-100', icon: Users },
  on_track: { label: 'On Track', color: 'text-emerald-600 bg-emerald-100', icon: Flag },
  complete: { label: 'Complete', color: 'text-blue-600 bg-blue-100', icon: CheckCircle2 },
};

// Mock races data
const mockRaces = [
  {
    id: 1,
    name: 'Heat Race 1',
    type: 'heat',
    status: 'complete',
    laps: 8,
    scheduled_time: '7:00 PM',
    cars: 10,
  },
  {
    id: 2,
    name: 'Heat Race 2',
    type: 'heat',
    status: 'complete',
    laps: 8,
    scheduled_time: '7:15 PM',
    cars: 10,
  },
  {
    id: 3,
    name: 'Heat Race 3',
    type: 'heat',
    status: 'on_track',
    laps: 8,
    current_lap: 5,
    scheduled_time: '7:30 PM',
    cars: 10,
  },
  {
    id: 4,
    name: 'Heat Race 4',
    type: 'heat',
    status: 'staging',
    laps: 8,
    scheduled_time: '7:45 PM',
    cars: 10,
  },
  {
    id: 5,
    name: 'B-Main',
    type: 'b-main',
    status: 'upcoming',
    laps: 12,
    scheduled_time: '8:15 PM',
    cars: 16,
  },
  {
    id: 6,
    name: 'A-Main',
    type: 'feature',
    status: 'upcoming',
    laps: 25,
    scheduled_time: '9:00 PM',
    cars: 24,
  },
];

// Mock lineup data for staging race
const mockLineup = [
  { position: 1, row: 1, car_number: '1A', driver: 'Brad Sweet', team: 'Kasey Kahne Racing', status: 'staged' },
  { position: 2, row: 1, car_number: '49', driver: 'Donny Schatz', team: 'Tony Stewart Racing', status: 'staged' },
  { position: 3, row: 2, car_number: '2', driver: 'David Gravel', team: 'Big Game Motorsports', status: 'staged' },
  { position: 4, row: 2, car_number: '17', driver: 'Sheldon Haudenschild', team: 'Stenhouse Jr-Marshall Racing', status: 'staging' },
  { position: 5, row: 3, car_number: '41', driver: 'Carson Macedo', team: 'Jason Johnson Racing', status: 'staging' },
  { position: 6, row: 3, car_number: '83', driver: 'James McFadden', team: 'Roth Motorsports', status: 'not_staged' },
  { position: 7, row: 4, car_number: '5', driver: 'Spencer Bayston', team: 'CJB Motorsports', status: 'not_staged' },
  { position: 8, row: 4, car_number: '18', driver: 'Giovanni Scelzi', team: 'KCP Racing', status: 'not_staged' },
  { position: 9, row: 5, car_number: '7S', driver: 'Robbie Price', team: 'Sides Motorsports', status: 'not_staged' },
  { position: 10, row: 5, car_number: '9', driver: 'Kasey Kahne', team: 'Kasey Kahne Racing', status: 'not_staged' },
];

// Staging status colors
const stagingStatusColors = {
  staged: 'bg-emerald-500',
  staging: 'bg-amber-500 animate-pulse',
  not_staged: 'bg-slate-300',
};

// Race type colors
const raceTypeColors = {
  heat: 'text-orange-600 bg-orange-100',
  'b-main': 'text-purple-600 bg-purple-100',
  feature: 'text-emerald-600 bg-emerald-100',
  qualifier: 'text-blue-600 bg-blue-100',
  'last-chance': 'text-red-600 bg-red-100',
};

// Race Card Component
function RaceCard({ race, isSelected, onClick }) {
  const statusConfig = raceStatuses[race.status];
  const StatusIcon = statusConfig.icon;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${
        isSelected
          ? 'border-rms-accent bg-rms-accent/5 ring-2 ring-rms-accent/20'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium px-2 py-0.5 rounded ${raceTypeColors[race.type] || 'text-slate-600 bg-slate-100'}`}>
          {race.type.toUpperCase()}
        </span>
        <span className={`flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${statusConfig.color}`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </span>
      </div>
      <h3 className="font-semibold text-slate-800">{race.name}</h3>
      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
        <span>{race.scheduled_time}</span>
        <span>•</span>
        <span>{race.laps} laps</span>
        <span>•</span>
        <span>{race.cars} cars</span>
      </div>
      {race.status === 'on_track' && race.current_lap && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${(race.current_lap / race.laps) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium text-slate-600">
            {race.current_lap}/{race.laps}
          </span>
        </div>
      )}
    </button>
  );
}

// Lineup Row Component (Grid View)
function LineupGridRow({ entry }) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border ${
      entry.status === 'staged' ? 'bg-emerald-50 border-emerald-200' :
      entry.status === 'staging' ? 'bg-amber-50 border-amber-200' :
      'bg-white border-slate-200'
    }`}>
      {/* Position */}
      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center font-bold text-sm">
        {entry.position}
      </div>

      {/* Car Number */}
      <div className="w-16 h-12 rounded-lg bg-white border-2 border-slate-800 flex items-center justify-center">
        <span className="font-mono font-bold text-xl text-slate-800">{entry.car_number}</span>
      </div>

      {/* Driver Info */}
      <div className="flex-1">
        <div className="font-semibold text-slate-800">{entry.driver}</div>
        <div className="text-sm text-slate-500">{entry.team}</div>
      </div>

      {/* Status Indicator */}
      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded-full ${stagingStatusColors[entry.status]}`} />
        <span className={`text-sm font-medium ${
          entry.status === 'staged' ? 'text-emerald-600' :
          entry.status === 'staging' ? 'text-amber-600' :
          'text-slate-400'
        }`}>
          {entry.status === 'staged' ? 'Staged' :
           entry.status === 'staging' ? 'Staging' :
           'Not Staged'}
        </span>
      </div>
    </div>
  );
}

// Lineup Row Component (Compact List View)
function LineupListRow({ entry }) {
  return (
    <div className={`flex items-center gap-3 px-4 py-2 border-b last:border-b-0 ${
      entry.status === 'staged' ? 'bg-emerald-50' :
      entry.status === 'staging' ? 'bg-amber-50' :
      'bg-white'
    }`}>
      <div className={`w-2 h-2 rounded-full ${stagingStatusColors[entry.status]}`} />
      <span className="w-6 text-sm font-medium text-slate-500">{entry.position}</span>
      <span className="w-12 font-mono font-bold text-slate-800">{entry.car_number}</span>
      <span className="flex-1 text-sm text-slate-700">{entry.driver}</span>
      <span className="text-xs text-slate-400">Row {entry.row}</span>
    </div>
  );
}

// Grid Formation View
function GridFormationView({ lineup }) {
  // Group by rows
  const rows = [];
  for (let i = 0; i < lineup.length; i += 2) {
    rows.push(lineup.slice(i, i + 2));
  }

  return (
    <div className="space-y-3">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4">
          <div className="w-16 text-center text-sm font-medium text-slate-400">
            Row {rowIndex + 1}
          </div>
          <div className="flex-1 grid grid-cols-2 gap-4">
            {row.map((entry, idx) => (
              <div
                key={entry.position}
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  entry.status === 'staged' ? 'bg-emerald-50 border-emerald-200' :
                  entry.status === 'staging' ? 'bg-amber-50 border-amber-200 animate-pulse' :
                  'bg-slate-50 border-slate-200'
                }`}
              >
                <div className={`w-12 h-10 rounded bg-white border-2 ${
                  entry.status === 'staged' ? 'border-emerald-500' :
                  entry.status === 'staging' ? 'border-amber-500' :
                  'border-slate-300'
                } flex items-center justify-center`}>
                  <span className="font-mono font-bold text-lg">{entry.car_number}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{entry.driver}</div>
                  <div className="text-xs text-slate-500 truncate">{entry.team}</div>
                </div>
                <div className={`w-3 h-3 rounded-full ${stagingStatusColors[entry.status]}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LineupMonitor() {
  useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  const [selectedRace, setSelectedRace] = useState(mockRaces[3]); // Start with staging race
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', 'formation'
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  // eslint-disable-next-line no-unused-vars
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Simulated auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setLastRefresh(new Date());
      // In real app, would fetch latest data here
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const stagedCount = mockLineup.filter(e => e.status === 'staged').length;
  const totalCount = mockLineup.length;

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <Link
            to={`/${seriesSlug}/tools`}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-500" />
          </Link>
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Monitor className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="page-title">Lineup Monitor</h1>
            <p className="page-subtitle">Real-time race lineup and staging status</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Auto Refresh Toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              autoRefresh
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-slate-50 border-slate-200 text-slate-600'
            }`}
          >
            {autoRefresh ? (
              <>
                <Zap className="w-4 h-4" />
                <span className="text-sm font-medium">Live</span>
              </>
            ) : (
              <>
                <Pause className="w-4 h-4" />
                <span className="text-sm font-medium">Paused</span>
              </>
            )}
          </button>

          {/* Manual Refresh */}
          <Button variant="secondary" onClick={() => setLastRefresh(new Date())}>
            <RefreshCw className="w-4 h-4" />
          </Button>

          {/* Fullscreen */}
          <Button variant="secondary" onClick={handleFullscreen}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Race List Sidebar */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Race Schedule</CardTitle>
              <CardDescription>Select a race to view lineup</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {mockRaces.map(race => (
                <RaceCard
                  key={race.id}
                  race={race}
                  isSelected={selectedRace?.id === race.id}
                  onClick={() => setSelectedRace(race)}
                />
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Selected Race Header */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-lg ${raceTypeColors[selectedRace?.type] || 'bg-slate-100'}`}>
                    <span className="font-semibold">{selectedRace?.type?.toUpperCase()}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selectedRace?.name}</h2>
                    <div className="flex items-center gap-3 text-sm text-slate-500">
                      <span>{selectedRace?.scheduled_time}</span>
                      <span>•</span>
                      <span>{selectedRace?.laps} laps</span>
                      <span>•</span>
                      <span>{selectedRace?.cars} cars</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  {/* Staging Progress */}
                  {selectedRace?.status === 'staging' && (
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="text-2xl font-bold text-slate-800">
                          {stagedCount}/{totalCount}
                        </div>
                        <div className="text-xs text-slate-500">Staged</div>
                      </div>
                      <div className="w-24 h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${(stagedCount / totalCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* View Mode Toggle */}
                  <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                      title="Grid View"
                    >
                      <Grid3X3 className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                      title="List View"
                    >
                      <List className="w-4 h-4 text-slate-600" />
                    </button>
                    <button
                      onClick={() => setViewMode('formation')}
                      className={`p-2 ${viewMode === 'formation' ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                      title="Formation View"
                    >
                      <Car className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lineup Display */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Starting Lineup</CardTitle>
                  <CardDescription>
                    Last updated: {lastRefresh.toLocaleTimeString()}
                  </CardDescription>
                </div>
                {selectedRace?.status === 'staging' && (
                  <Badge variant="warning" className="animate-pulse">
                    <Users className="w-3 h-3 mr-1" />
                    Staging in Progress
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {viewMode === 'grid' && (
                <div className="space-y-3">
                  {mockLineup.map(entry => (
                    <LineupGridRow key={entry.position} entry={entry} />
                  ))}
                </div>
              )}

              {viewMode === 'list' && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500">
                    <span className="w-2" />
                    <span className="w-6">Pos</span>
                    <span className="w-12">Car</span>
                    <span className="flex-1">Driver</span>
                    <span>Row</span>
                  </div>
                  {mockLineup.map(entry => (
                    <LineupListRow key={entry.position} entry={entry} />
                  ))}
                </div>
              )}

              {viewMode === 'formation' && (
                <GridFormationView lineup={mockLineup} />
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-slate-600">Staged</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span className="text-slate-600">Staging</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-300" />
              <span className="text-slate-600">Not Staged</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
